import type { MessageButton } from './bots.js';
import type { OfficialCardDto } from './officialChannels.js';
import type { QuakeCardDto } from './quakeChannel.js';
/**
 * Messaging contracts shared by API, realtime gateway and clients (REALTIME_PROTOCOL.md, ADR-002).
 * Phase 2 bodies are plaintext on the server; `ciphertext` replaces `body` when E2EE lands (Phase 5).
 */
import { z } from 'zod';
import type { ChatAppearance } from './appearance.js';
import { MESSAGE_BODY_HARD_LIMIT, PROTOCOL_VERSION, INVITE_LINK_EXPIRY_HOURS, MAX_FORWARD_TARGETS, TEMPLATE_CATEGORIES, AI_ACTIONS } from './constants.js';
import type { ErrorCode } from './errors.js';
import type { MeydanNewPostEventDto, MeydanPostStateDto } from './meydan.js';
import { STICKER_IDS } from './stickers.js';

/** Megolm room-event content produced by the client crypto library; opaque to the server (ADR-014). */
export const megolmContentSchema = z
  .object({
    algorithm: z.literal('m.megolm.v1.aes-sha2'),
    ciphertext: z.string().min(1).max(64 * 1024),
    session_id: z.string().min(1).max(128),
    sender_key: z.string().max(128).optional(),
    device_id: z.string().max(128).optional(),
  })
  .strict();
export type MegolmContent = z.infer<typeof megolmContentSchema>;

const sendMessageFields = z.object({
  clientMsgId: z.uuid(),
  /** Text, or the caption of an attachment (may then be empty). */
  body: z.string().max(MESSAGE_BODY_HARD_LIMIT).default(''),
  attachmentId: z.uuid().optional(),
  replyToId: z.uuid().optional(),
  /** A URL from the text whose server-fetched preview should be attached (ignored if none is cached). */
  linkPreviewUrl: z.url({ protocol: /^https?$/ }).max(2048).optional(),
  /** End-to-end encrypted content; the body must then be empty. */
  encrypted: megolmContentSchema.optional(),
  /** A copy forwarded by the device (encrypted chats forward by decrypting and sending again). */
  forwarded: z.boolean().optional(),
  /** Encrypted chats: who is named with @ (the server cannot read the text). Ignored for plaintext. */
  mentions: z.array(z.uuid()).max(50).optional(),
  /** A sticker from the built-in pack (plaintext chats; encrypted chats carry it inside the ciphertext). */
  sticker: z.enum(STICKER_IDS).optional(),
  /** A location (plaintext chats; encrypted chats carry it inside the ciphertext). */
  location: z.lazy(() => locationPointSchema).optional(),
  /** A contact card: share this person (plaintext chats; encrypted chats carry it inside the ciphertext). */
  contactUserId: z.uuid().optional(),
  /** Live location: the sender keeps updating this message for 15 minutes, 1 hour or 8 hours. */
  liveMinutes: z.union([z.literal(15), z.literal(60), z.literal(480)]).optional(),
  /** View once: each recipient can open the photo/video once (needs an attachment, no caption). */
  viewOnce: z.boolean().optional(),
  /** A reply in the thread of this message (groups), or a comment under a channel post. */
  threadRootId: z.uuid().optional(),
  clientSentAt: z.iso.datetime().optional(),
});
const notEmpty = (m: {
  body: string;
  attachmentId?: string | undefined;
  encrypted?: MegolmContent | undefined;
  sticker?: string | undefined;
  location?: unknown;
  contactUserId?: string | undefined;
  liveMinutes?: number | undefined;
  viewOnce?: boolean | undefined;
}) => {
  // View once: a photo or video alone (encrypted chats keep the file inside the ciphertext, plus its upload).
  if (m.viewOnce) return Boolean(m.attachmentId) && m.body.length === 0 && !m.sticker && !m.location && !m.contactUserId && !m.liveMinutes;
  // Stickers, locations and contact cards travel alone: no text, no file, one of them at a time.
  const special = [m.sticker, m.location, m.contactUserId].filter(Boolean).length;
  if (m.encrypted) return m.body.length === 0 && special === 0;
  if (special > 0) return special === 1 && m.body.length === 0 && !m.attachmentId;
  return !m.liveMinutes && (m.body.trim().length > 0 || Boolean(m.attachmentId));
};
export const sendMessageSchema = sendMessageFields.refine(notEmpty, { message: 'empty', path: ['body'] });

/** A point on the map; accuracy in metres. */
export const locationPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracy: z.number().min(0).max(100_000).nullable().default(null),
});
export type LocationPoint = z.infer<typeof locationPointSchema>;
export interface LocationDto extends LocationPoint {
  updatedAt: string;
}
export type SendMessageInput = z.infer<typeof sendMessageSchema>;

// ---------- polls, scheduled and disappearing messages ----------

export const MAX_POLL_OPTIONS = 12;
export const createPollSchema = z
  .object({
    clientMsgId: z.uuid(),
    question: z.string().trim().min(1).max(300),
    options: z.array(z.string().trim().min(1).max(100)).min(2).max(MAX_POLL_OPTIONS),
    multiple: z.boolean().default(false),
    anonymous: z.boolean().default(false),
    /** A copy of a poll from another chat, sent again by the device: marked forwarded, with no votes. */
    forwarded: z.boolean().optional(),
  })
  .refine((p) => new Set(p.options.map((o) => o.toLocaleLowerCase('tr'))).size === p.options.length, { message: 'duplicate option', path: ['options'] });
export type CreatePollInput = z.infer<typeof createPollSchema>;
/** My choice (an empty list takes my vote back). */
export const pollVoteSchema = z.object({ optionIds: z.array(z.number().int().min(0).max(MAX_POLL_OPTIONS - 1)).max(MAX_POLL_OPTIONS) });

export interface PollOptionDto {
  id: number;
  text: string;
  votes: number;
  /** Who chose it; null in anonymous polls. */
  voterIds: string[] | null;
}
export interface PollDto {
  question: string;
  options: PollOptionDto[];
  multiple: boolean;
  anonymous: boolean;
  closed: boolean;
  totalVoters: number;
  /** My own choice. Only in answers meant for me; absent in updates sent to everyone (keep the one you have). */
  myVotes?: number[];
}

export type RsvpAnswer = 'going' | 'maybe' | 'no';
/** An event shared in a chat. Counts come from the answers table; `goingIds` lists who is coming. */
export interface ChatEventDto {
  title: string;
  startsAt: string;
  endsAt: string | null;
  location: string | null;
  description: string | null;
  cancelled: boolean;
  going: number;
  maybe: number;
  no: number;
  goingIds: string[];
  /** An occurrence of a Seri Etkinlikler series (Market app): which series, and where it meets. */
  series?: { id: string; key: number; cover: string; place: 'none' | 'link' | 'call'; link: string | null };
  /** My own answer. Only in answers meant for me; absent in updates sent to everyone (keep the one you have). */
  myRsvp?: RsvpAnswer | null;
}
export const createChatEventSchema = z
  .object({
    clientMsgId: z.uuid(),
    title: z.string().trim().min(1).max(120),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }).nullable().default(null),
    location: z.string().trim().max(200).nullable().default(null),
    description: z.string().trim().max(1000).nullable().default(null),
    /** A copy of an event from another chat, sent again by the device: marked forwarded, with no answers. */
    forwarded: z.boolean().optional(),
  })
  .refine((e) => !e.endsAt || Date.parse(e.endsAt) > Date.parse(e.startsAt), { message: 'ends before it starts', path: ['endsAt'] });
export type CreateChatEventInput = z.infer<typeof createChatEventSchema>;
export const rsvpSchema = z.object({ answer: z.enum(['going', 'maybe', 'no']).nullable() });

/** A line the server writes into the chat. */
export type SystemNoticeDto = { kind: 'timer'; seconds: number | null; byUserId: string };

/** Disappearing messages: 24 hours, 7 days, 90 days. */
export const MESSAGE_TTL_OPTIONS = [86_400, 604_800, 7_776_000] as const;
export const messageTimerSchema = z.object({
  seconds: z.union([z.literal(86_400), z.literal(604_800), z.literal(7_776_000)]).nullable(),
});

export const MAX_SCHEDULED_PER_CHAT = 50;
export const SCHEDULE_MAX_DAYS = 365;
const sendAtField = z.iso.datetime({ offset: true });
export const scheduleMessageSchema = sendMessageFields
  .pick({ clientMsgId: true, body: true, encrypted: true, replyToId: true, mentions: true })
  .extend({ sendAt: sendAtField })
  .refine((m) => (m.encrypted ? m.body.length === 0 : m.body.trim().length > 0), { message: 'empty', path: ['body'] });
export const updateScheduledSchema = z
  .object({
    body: z.string().max(MESSAGE_BODY_HARD_LIMIT).refine((v) => v.trim().length > 0, 'empty'),
    encrypted: megolmContentSchema,
    sendAt: sendAtField,
  })
  .partial()
  .strict()
  .refine((v) => !(v.body !== undefined && v.encrypted !== undefined), { message: 'either body or encrypted', path: ['body'] });

export interface ScheduledMessageDto {
  id: string;
  conversationId: string;
  body: string;
  encrypted: MegolmContent | null;
  replyToId: string | null;
  sendAt: string;
  /** Could not be sent at its time (e.g. no longer allowed to write here); kept so I can see why. */
  failed: boolean;
  errorCode: string | null;
}

export const editMessageSchema = z
  .object({
    body: z.string().max(MESSAGE_BODY_HARD_LIMIT).refine((s) => s.trim().length > 0, 'empty').optional(),
    /** The new content, encrypted on the device (encrypted chats). */
    encrypted: megolmContentSchema.optional(),
  })
  .refine((v) => (v.body === undefined) !== (v.encrypted === undefined), { message: 'either body or encrypted', path: ['body'] });

/** My reaction in an encrypted chat (null removes it). Small: one emoji and the target, encrypted. */
export const encryptedReactionSchema = z.object({
  encrypted: megolmContentSchema.refine((c) => c.ciphertext.length <= 4096, 'too large').nullable(),
});
export const deleteMessageQuerySchema = z.object({ scope: z.enum(['everyone', 'me']).default('me') });

export const createGroupSchema = z.object({
  title: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  memberIds: z.array(z.uuid()).max(255).default([]),
});
export const updateGroupSchema = z
  .object({
    title: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).nullable(),
    onlyAdminsCanSend: z.boolean(),
    commentsEnabled: z.boolean(),
    slowModeSeconds: z.number().int().min(0).max(3600),
    membersCanSendMedia: z.boolean(),
    membersCanAddMembers: z.boolean(),
    membersCanEditInfo: z.boolean(),
    /** Channels: which reactions followers may leave. */
    reactionsMode: z.enum(['all', 'basic', 'off']),
  })
  .partial()
  .strict();
export const addMembersSchema = z.object({ userIds: z.array(z.uuid()).min(1).max(100) });
export const memberRoleSchema = z.object({ role: z.enum(['admin', 'moderator', 'member']) });
export const createInviteLinkSchema = z.object({
  requireApproval: z.boolean().default(false),
  expiresInHours: z
    .number()
    .int()
    .refine((h) => (INVITE_LINK_EXPIRY_HOURS as readonly number[]).includes(h), 'unsupported')
    .nullable()
    .default(null),
});
/** Invite tokens travel in request bodies (never URLs), so they stay out of access logs. */
export const groupLinkTokenSchema = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{32,64}$/) });
/** A call link: like an invite link, plus the kind of call it opens (default: valid for 30 days). */
export const createCallLinkSchema = createInviteLinkSchema.extend({
  media: z.enum(['audio', 'video']),
  expiresInHours: z
    .number()
    .int()
    .refine((h) => (INVITE_LINK_EXPIRY_HOURS as readonly number[]).includes(h), 'unsupported')
    .nullable()
    .default(720),
});
export const joinDecisionSchema = z.object({ approve: z.boolean() });

/** Pinned messages per conversation. */
export const MAX_PINNED_MESSAGES = 3;
/** Pending "remind me" reminders a person may hold at once. */
export const MAX_MESSAGE_REMINDERS = 100;
/** One emoji (including ZWJ sequences, skin tones, flags, subdivision flags and keycaps); no text. */
const EMOJI = /^(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Regional_Indicator}|\u200d|\ufe0f|\u20e3|[\u{E0020}-\u{E007F}]|[#*0-9])+$/u;
/** Longest single emoji in UTF-16 units: a family or couple with skin tones, or a subdivision flag. */
export const MAX_EMOJI_LENGTH = 32;
/** The quick reactions (one tap under a message); a channel in "basic" mode takes only these. */
export const BASIC_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;
/** A channel's reaction setting: every emoji, only BASIC_REACTIONS, or none. */
export type ChannelReactionsMode = 'all' | 'basic' | 'off';
export const reactionSchema = z.object({
  emoji: z
    .string()
    .min(1)
    .max(MAX_EMOJI_LENGTH)
    .refine((e) => EMOJI.test(e) && /\p{Extended_Pictographic}|\p{Regional_Indicator}|\u20e3/u.test(e), 'not an emoji')
    .refine((e) => [...new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(e)].length === 1, 'one emoji'),
});
export const forwardSchema = z.object({ conversationIds: z.array(z.uuid()).min(1).max(MAX_FORWARD_TARGETS) });

export const linkPreviewRequestSchema = z.object({ url: z.url({ protocol: /^https?$/ }).max(2048) });

export const receiptSchema = z.object({ seq: z.number().int().nonnegative() });
export const usernameRefSchema = z.object({ username: z.string().min(1).max(64) });
export const userRefSchema = z.object({ userId: z.uuid() });
/** Catch-up of edits, deletions and reactions after a reconnect (server time from GET /sync). */
export const changesQuerySchema = z.object({ since: z.iso.datetime({ offset: true }) });

/** GET /conversations/:id/messages/at?at=… → the first message on or after that moment (jump to a date). */
export const messageAtQuerySchema = z.object({ at: z.iso.datetime({ offset: true }) });
export interface MessageAtDto {
  /** null: nothing on or after that moment (the newest messages are shown instead). */
  seq: number | null;
  messageId: string | null;
  /** The oldest message this member can see, so the date picker does not offer days before it. */
  firstAt: string | null;
}

export const historyQuerySchema = z.object({
  beforeSeq: z.coerce.number().int().positive().optional(),
  afterSeq: z.coerce.number().int().nonnegative().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export interface AttachmentDto {
  id: string;
  kind: 'image' | 'file' | 'voice' | 'video';
  mime: string;
  size: number;
  name: string;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** Voice notes: loudness samples 0–100 recorded on the device, for the waveform in the bubble. */
  waveform?: number[];
  hasThumbnail: boolean;
  /** A round video note (a short square video shown as a circle). */
  round?: boolean;
  /** A sticker someone made ("Yeni çıkartma"): shown without a bubble. */
  sticker?: boolean;
  /** A chat video the server is still re-encoding (the original plays meanwhile; the same id serves the new file). */
  processing?: boolean;
}

/** Someone from my address book who is on the app (POST /contacts/match). */
export interface ContactMatchDto {
  /** The digest I sent, so the device knows which of its entries this is. */
  hash: string;
  user: UserSummaryDto;
  /** Already in my contacts. */
  isContact: boolean;
}

/** Who a contact card points at, as it was when shared. */
export interface ContactCardDto {
  id: string;
  username: string;
  displayName: string;
}

export interface UserSummaryDto {
  id: string;
  username: string;
  displayName: string;
  /** A bot account (driven over the Bot API; its username ends with "bot"). */
  isBot?: boolean;
}

export interface ContactDto extends UserSummaryDto {
  addedAt: string;
  /** True when the caller has blocked this user. */
  blocked: boolean;
}

export interface MessageDto {
  id: string;
  conversationId: string;
  seq: number;
  senderId: string;
  clientMsgId: string;
  contentType: 'text' | 'image' | 'file' | 'voice' | 'video' | 'encrypted' | 'poll' | 'system' | 'sticker' | 'location' | 'contact' | 'event';
  body: string | null;
  /** Megolm ciphertext of an end-to-end encrypted message (body is then null); null once deleted. */
  encrypted: MegolmContent | null;
  /** Present for image/file/voice messages (null once deleted). */
  attachment: AttachmentDto | null;
  replyToId: string | null;
  /** A forwarded copy (the original sender is not revealed). */
  forwarded: boolean;
  /** Grouped by emoji; empty once deleted. Channels: counts only (`count`, `userIds` empty). */
  reactions: ReactionDto[];
  /**
   * Channel posts: my own reaction (null: none). Absent on updates that go to every follower alike: keep the one
   * already known.
   */
  myReaction?: string | null;
  /** Encrypted chats: each person's reaction, encrypted (decrypted and counted on the device). */
  encryptedReactions: { userId: string; encrypted: MegolmContent }[];
  /** A reply in a thread (or a comment under a channel post): not shown in the main timeline. */
  threadRootId?: string | null;
  /** On a thread's first message: how many replies, and when the last one came. */
  thread?: { count: number; lastAt: string | null };
  /** Encrypted chats: each person's vote on a poll or answer to an event, encrypted on their device. */
  encryptedAnswers?: { userId: string; encrypted: MegolmContent }[];
  /** Server-fetched preview of a link in the text. */
  linkPreview: LinkPreviewDto | null;
  createdAt: string;
  editedAt: string | null;
  /** Deleted for everyone: body is null and the content is gone from the server. */
  deleted: boolean;
  /** A poll (body holds the question too, for previews and search). */
  poll: PollDto | null;
  /** An event with answers (plaintext chats; null once deleted). */
  event?: ChatEventDto | null;
  /** Members named with @. */
  mentions: string[];
  /** Disappearing message: gone from every device at this time. */
  expiresAt: string | null;
  /** A notice written by the server (contentType 'system'). */
  system: SystemNoticeDto | null;
  /** A shared location (plaintext chats; encrypted ones carry it in the ciphertext). */
  location: LocationDto | null;
  /** A shared contact card (plaintext chats; encrypted ones carry it in the ciphertext). */
  contact: ContactCardDto | null;
  /** Inline buttons under a bot's message (rows of callback or link buttons). */
  buttons: MessageButton[][] | null;
  /** Live location: updated by the sender until this time (null once stopped or for a single point). */
  liveUntil: string | null;
  /** A view-once photo/video: who has opened it, and whether the file is gone (all opened, or 14 days passed). */
  viewOnce: ViewOnceDto | null;
  /** Son Depremler: the card the server posted (earthquake, summary or safety text); body keeps the readable text. */
  quake?: QuakeCardDto | OfficialCardDto;
}

export interface ViewOnceDto {
  openedBy: string[];
  gone: boolean;
}

/** Unopened view-once files are erased after this long. */
export const VIEW_ONCE_TTL_DAYS = 14;

export interface LinkPreviewDto {
  id: string;
  url: string;
  title: string | null;
  description: string | null;
  siteName: string | null;
  hasImage: boolean;
}

export type DeliveryState = 'sent' | 'delivered' | 'read';

export interface MessageInfoDto {
  members: (UserSummaryDto & { status: DeliveryState })[];
  /** True when read states are withheld (the viewer does not share read receipts). */
  readsHidden: boolean;
}

export interface StarredMessageDto {
  message: MessageDto;
  starredAt: string;
}

/** "Bana hatırlat": a private reminder about a message; a push opens the message when it is due. */
export interface ReminderDto {
  id: string;
  messageId: string;
  conversationId: string;
  remindAt: string;
  message: MessageDto;
}
export const setReminderSchema = z.object({ at: z.iso.datetime({ offset: true }) });

export interface ReactionDto {
  emoji: string;
  /** Who chose it (chats and groups); always empty in channels, where nobody sees who reacted. */
  userIds: string[];
  /** Channels: how many chose it. */
  count?: number;
}

export interface PinnedMessageDto {
  messageId: string;
  seq: number;
  senderId: string;
  contentType: MessageDto['contentType'];
  /** First characters of the text (null for attachments without caption). */
  preview: string | null;
  pinnedAt: string;
}

/** What a person chose (auto = follow what they do). */
export const PRESENCE_CHOICES = ['auto', 'dnd', 'busy', 'away', 'invisible'] as const;
export type PresenceChoice = (typeof PRESENCE_CHOICES)[number];
/** What others see. in_call = video call, on_call = voice call. */
export type PresenceStatus = 'online' | 'idle' | 'dnd' | 'busy' | 'away' | 'in_call' | 'on_call' | 'offline';

/** A status chosen from the panel's list, as others see it (names in both languages; the viewer picks). */
export interface PresenceCustomDto {
  nameTr: string;
  nameEn: string;
  icon: StatusIcon;
  color: string;
}

export interface PresenceDto {
  userId: string;
  online: boolean;
  /** Only when offline and visible to the viewer. */
  lastSeenAt: string | null;
  status: PresenceStatus;
  /** The panel status behind `status` (Toplantıda, Tatilde…), when one is shown. */
  custom?: PresenceCustomDto | null;
}

/** Icons a status can have (panel › Durumlar). */
export const STATUS_ICONS = ['minus', 'circle', 'clock', 'eyeOff', 'calendar', 'target', 'palmtree', 'thermometer', 'car', 'coffee', 'home', 'book', 'headphones', 'plane', 'moon', 'briefcase', 'heart', 'dumbbell', 'utensils', 'gamepad', 'graduation', 'stethoscope', 'baby'] as const;
export type StatusIcon = (typeof STATUS_ICONS)[number];
/** What a status does: dnd silences message notifications, invisible looks offline, busy and away only show. */
export const PRESENCE_BEHAVIORS = ['dnd', 'busy', 'away', 'invisible'] as const;
export type PresenceBehavior = (typeof PRESENCE_BEHAVIORS)[number];
export const STATUS_PLANS = ['plus', 'business', 'premium'] as const;

/** A status I may pick, in my language. */
export interface PresenceTypeDto {
  id: string;
  slug: string;
  behavior: PresenceBehavior;
  name: string;
  description: string;
  icon: StatusIcon;
  color: string;
  minPlan: (typeof STATUS_PLANS)[number] | null;
}

/** My own status: what I chose (and until when) and what others see now. */
export interface MyPresenceDto {
  choice: PresenceChoice;
  typeId: string | null;
  type: PresenceTypeDto | null;
  until: string | null;
  status: PresenceStatus | 'invisible';
}
export const setPresenceSchema = z.object({
  /** A status from the list (null = automatic). */
  typeId: z.uuid().nullable().optional(),
  /** Older apps send the behaviour instead; it maps to the built-in status. */
  choice: z.enum(PRESENCE_CHOICES).optional(),
  /** When it goes back to automatic; null = until I change it. */
  until: z.iso.datetime({ offset: true }).nullable().default(null),
});

/** Panel › Durumlar. */
export interface AdminPresenceTypeDto {
  id: string;
  slug: string;
  behavior: PresenceBehavior;
  nameTr: string;
  nameEn: string;
  descriptionTr: string;
  descriptionEn: string;
  icon: StatusIcon;
  color: string;
  priority: number;
  active: boolean;
  minPlan: (typeof STATUS_PLANS)[number] | null;
  audience: 'everyone' | 'chosen';
  system: boolean;
  /** People who may use it when the audience is "chosen". */
  people: { id: string; username: string; displayName: string }[];
  /** People using it right now. */
  inUse: number;
}
const statusHex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const presenceTypeInputSchema = z.object({
  slug: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{1,31}$/),
  behavior: z.enum(PRESENCE_BEHAVIORS),
  nameTr: z.string().trim().min(1).max(40),
  nameEn: z.string().trim().min(1).max(40),
  descriptionTr: z.string().trim().max(160).default(''),
  descriptionEn: z.string().trim().max(160).default(''),
  icon: z.enum(STATUS_ICONS),
  color: statusHex,
  priority: z.number().int().min(0).max(1000).default(100),
  active: z.boolean().default(true),
  minPlan: z.enum(STATUS_PLANS).nullable().default(null),
  audience: z.enum(['everyone', 'chosen']).default('everyone'),
  /** User ids (audience "chosen"). */
  people: z.array(z.uuid()).max(500).default([]),
});
export type PresenceTypeInput = z.input<typeof presenceTypeInputSchema>;
export const presenceTypeUpdateSchema = presenceTypeInputSchema.omit({ slug: true }).partial().strict();

/** Owner > admin > moderator > member. Moderators pin, delete others' messages and remove members; admins also change settings and roles. */
export type GroupRole = 'owner' | 'admin' | 'moderator' | 'member';
export const GROUP_ROLE_RANK: Record<GroupRole, number> = { member: 0, moderator: 1, admin: 2, owner: 3 };
/** Slow mode choices (seconds between a member's messages); 0 is off. */
export const SLOW_MODE_OPTIONS = [0, 10, 30, 60, 300, 900, 3600] as const;

export interface GroupInfoDto {
  title: string;
  description: string | null;
  /** When the group photo last changed (part of its address); null when there is none. */
  avatarUpdatedAt: string | null;
  /** The look the admins chose for everyone (each person may still pick their own for this chat). */
  look: ChatAppearance | null;
  memberCount: number;
  myRole: GroupRole;
  onlyAdminsCanSend: boolean;
  /** Channels: subscribers may comment under posts. */
  commentsEnabled: boolean;
  /** Channels: which reactions followers may leave (absent for groups, which take every emoji). */
  reactionsMode?: ChannelReactionsMode;
  /** Seconds a plain member waits between their own messages; 0 is off. */
  slowModeSeconds: number;
  /** What plain members may do (owner, admins and moderators always may). */
  membersCanSendMedia: boolean;
  membersCanAddMembers: boolean;
  membersCanEditInfo: boolean;
  /** Staff silenced me in this group until then (I can read, not write). */
  silencedUntil: string | null;
  /** Pending join requests (0 unless the viewer is an owner or admin). */
  pendingRequests: number;
  /** Community (space) it belongs to, if any. */
  spaceId: string | null;
  /** The community's announcements channel. */
  isAnnouncements: boolean;
  /** Channel listed in the public directory. */
  isPublic: boolean;
  /** Community group that community members may join without an invite. */
  communityOpen: boolean;
  /** Its short public name (dijitalapp.com/@name), if it has one. A channel's is an old one: channels are shared by slug. */
  handle: string | null;
  /** A channel's link (web.dijitalapp.com/kanal/<slug>); absent for none. */
  slug?: string;
  /** A channel staff verified (the blue tick, as on verified accounts); absent otherwise. */
  verified?: boolean;
  /** When the group or channel was made (absent in chats cached before it was sent). */
  createdAt?: string;
}

// ---------- communities and channels (ADR-017) ----------

export const createChannelSchema = z.object({
  title: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  public: z.boolean().default(false),
});
export const channelDiscoverQuerySchema = z.object({ q: z.string().trim().max(80).optional() });
export interface ChannelSummaryDto {
  id: string;
  title: string;
  description: string | null;
  subscriberCount: number;
  subscribed: boolean;
  /** Verified by staff (the blue tick); absent otherwise. */
  verified?: boolean;
  /** When its photo changed (null: none); a public channel's photo shows to anyone (groupPhoto). */
  avatarUpdatedAt: string | null;
  /** Its link (/kanal/<slug>); absent for none. */
  slug?: string;
}

export const createCommunityGroupSchema = z.object({
  title: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  open: z.boolean().default(true),
});
export const spaceInviteLinkSchema = z.object({ expiresInHours: z.number().int().refine((h) => [24, 168, 720].includes(h), 'unsupported').nullable().default(null) });
export const spaceLinkTokenSchema = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{32,64}$/) });

export interface CommunityGroupDto {
  id: string;
  title: string;
  description: string | null;
  memberCount: number;
  open: boolean;
  joined: boolean;
}
export interface CommunityDto {
  spaceId: string;
  name: string;
  type: 'community' | 'family';
  myRole: 'owner' | 'admin' | 'member' | 'guest';
  memberCount: number;
  announcementsId: string;
  groups: CommunityGroupDto[];
}
export interface SpaceInvitePreviewDto {
  name: string;
  type: string;
  memberCount: number;
  /** Set when the viewer is already a member. */
  spaceId: string | null;
}

export interface GroupInviteLinkDto {
  /** Set for a call link: joining also opens the group's call of this kind. */
  callMedia?: CallMedia;
  createdAt: string;
  requireApproval: boolean;
  expiresAt: string | null;
  useCount: number;
}

export interface GroupInvitePreviewDto {
  title: string;
  description: string | null;
  memberCount: number;
  requireApproval: boolean;
  /** Set when the viewer is already a member. */
  conversationId: string | null;
  requested: boolean;
  /** A call link: the kind of call, and how many are in it right now (0 when none is running). */
  call?: { media: CallMedia; inCall: number };
}

export interface GroupJoinResultDto {
  status: 'joined' | 'requested';
  conversationId: string | null;
}

export interface GroupJoinRequestDto extends UserSummaryDto {
  requestedAt: string;
  /** The person was removed from this group by an admin before. */
  previouslyRemoved: boolean;
}

/** Someone banned from a group: they cannot come back until an admin lifts it. */
export interface GroupBanDto extends UserSummaryDto {
  bannedAt: string;
  /** Who banned them (display name), when still known. */
  bannedBy: string | null;
}

export interface GroupMemberDto extends UserSummaryDto {
  role: GroupRole;
  /** Staff silenced this member until then (they read but cannot write). */
  silencedUntil?: string | null;
}
/** Silence a member for a while (minutes), or lift it (null). */
export const silenceMemberSchema = z.object({ minutes: z.union([z.literal(60), z.literal(480), z.literal(1440), z.literal(10080)]).nullable() });
/** A line of a group's log, for its staff. */
export interface GroupLogEntryDto {
  at: string;
  action: string;
  actor: { id: string; displayName: string } | null;
  target: { id: string; displayName: string } | null;
  detail: string | null;
}

export interface ConversationDto {
  id: string;
  type: 'direct' | 'group' | 'channel';
  /** The other person in a direct conversation; null for groups. */
  peer: UserSummaryDto | null;
  /** The chat with yourself ("Kendime not"): peer is you. */
  self: boolean;
  /** Group details; null for direct conversations. */
  group: GroupInfoDto | null;
  /** Newest first, at most MAX_PINNED_MESSAGES. */
  pinned: PinnedMessageDto[];
  /** End-to-end encryption is on (irreversible): only encrypted messages are accepted. */
  e2ee: boolean;
  lastSeq: number;
  lastMessage: MessageDto | null;
  unreadCount: number;
  myLastReadSeq: number;
  peerLastDeliveredSeq: number;
  peerLastReadSeq: number;
  /** Whether the caller can currently send here (blocks, privacy, verification). */
  canSend: boolean;
  /** Notifications for this conversation are muted until this time (null = not muted). */
  mutedUntil: string | null;
  /** Push for every message, only when I am named or answered, or never. */
  notifyLevel: NotifyLevel;
  /** Pinned to the top of my chat list (newest pin first); null when not pinned. */
  pinnedAt: string | null;
  /** Moved to my archive; it stays there when new messages arrive. */
  archived: boolean;
  /** I marked it unread by hand (cleared when I open it). */
  markedUnread: boolean;
  /** I locked it: it lives in "Locked chats" and opens only on an unlocked device. */
  locked: boolean;
  /** Disappearing messages: seconds new messages live (null = off). */
  messageTtl: number | null;
  /** Unread messages that name me with @. */
  unreadMentions: number;
  /** My messages waiting to be sent here later. */
  scheduledCount: number;
  /**
   * Direct: a message request not yet accepted. `incoming`: someone I have not added wrote to me (it waits in
   * "Mesaj istekleri"); otherwise I wrote to them and may send at most MESSAGE_REQUEST_MAX messages until they accept.
   */
  request: { incoming: boolean; sent: number } | null;
  /**
   * Its place in my chat list (newest first, as in WhatsApp): the last message I can see, or else when I joined it
   * (a channel just followed, a group I was just added to). Left out by older servers and cached copies.
   */
  activityAt?: string;
}

/** Messages one may send to someone who has not accepted the request yet. */
export const MESSAGE_REQUEST_MAX = 3;

/** At most this many chats pinned to the top of the list (as in WhatsApp). */
export const MAX_PINNED_CHATS = 5;
export const MAX_CHAT_FOLDERS = 10;
export const MAX_FOLDER_CHATS = 200;
export const CHAT_FOLDER_TYPES = ['direct', 'group', 'channel'] as const;

/** My own list settings for one chat; each field is optional. */
export const NOTIFY_LEVELS = ['all', 'mentions', 'none'] as const;
export type NotifyLevel = (typeof NOTIFY_LEVELS)[number];
export const conversationStateSchema = z
  .object({ pinned: z.boolean(), archived: z.boolean(), unread: z.boolean(), notifyLevel: z.enum(NOTIFY_LEVELS) })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'empty');

export const chatFolderSchema = z
  .object({
    name: z.string().trim().min(1).max(32),
    emoji: z.string().trim().max(16).nullable().default(null),
    includeTypes: z.array(z.enum(CHAT_FOLDER_TYPES)).max(3).default([]),
    includeIds: z.array(z.uuid()).max(MAX_FOLDER_CHATS).default([]),
    excludeIds: z.array(z.uuid()).max(MAX_FOLDER_CHATS).default([]),
    excludeMuted: z.boolean().default(false),
    excludeRead: z.boolean().default(false),
  })
  .strict()
  .refine((f) => f.includeTypes.length > 0 || f.includeIds.length > 0, { message: 'empty folder', path: ['includeIds'] });
export type ChatFolderInput = z.infer<typeof chatFolderSchema>;
export const chatFolderOrderSchema = z.object({ ids: z.array(z.uuid()).max(MAX_CHAT_FOLDERS) });

export interface ChatFolderDto {
  id: string;
  name: string;
  emoji: string | null;
  includeTypes: (typeof CHAT_FOLDER_TYPES)[number][];
  includeIds: string[];
  excludeIds: string[];
  excludeMuted: boolean;
  excludeRead: boolean;
}

/** Whether a chat belongs in a folder (the same rule on every device). */
export function inChatFolder(f: ChatFolderDto, c: Pick<ConversationDto, 'id' | 'type' | 'mutedUntil' | 'unreadCount' | 'markedUnread'>): boolean {
  if (f.excludeIds.includes(c.id)) return false;
  if (f.includeIds.includes(c.id)) return true;
  if (!f.includeTypes.includes(c.type)) return false;
  if (f.excludeMuted && c.mutedUntil) return false;
  if (f.excludeRead && c.unreadCount === 0 && !c.markedUnread) return false;
  return true;
}

// ---------- realtime envelopes ----------

export interface ServerEnvelope<P = unknown> {
  version: typeof PROTOCOL_VERSION;
  event: string;
  event_id: string;
  conversation_id?: string;
  seq?: number;
  timestamp: string;
  payload: P;
}

export type ServerEvent =
  | ServerEnvelope<{ heartbeatIntervalMs: number; serverTime: string; limits: { maxFrameBytes: number } }> & { event: 'hello' }
  | ServerEnvelope<MessageDto> & { event: 'message.created' }
  | ServerEnvelope<MessageDto> & { event: 'message.updated' }
  /** A channel post's reaction counts changed (sent at most about once a second per post). */
  | ServerEnvelope<{ conversationId: string; messageId: string; reactions: ReactionDto[] }> & { event: 'message.reactions' }
  | ServerEnvelope<{ userId: string; deliveredSeq: number; readSeq: number }> & { event: 'receipt.updated' }
  | ServerEnvelope<{ userId: string; typing: boolean }> & { event: 'typing.updated' }
  | ServerEnvelope<PresenceDto> & { event: 'presence.updated' }
  | ServerEnvelope<{ conversationId: string }> & { event: 'conversation.updated' }
  /** Calendar events or tasks in a scope changed; clients refetch (ADR-018). */
  | ServerEnvelope<{ scope: { type: 'personal' | 'space' | 'group'; id: string | null } }> & { event: 'planner.updated' }
  /** Classes or homework changed; clients refetch (ADR-019). */
  | ServerEnvelope<{ classId: string | null }> & { event: 'learn.updated' }
  /** Someone I can see posted or removed a story; the stories strip refetches. */
  | ServerEnvelope<{ userId: string }> & { event: 'stories.updated' }
  /** Meydan: a post's counts (and my own state, on my devices) changed; every view shows the same numbers. */
  | ServerEnvelope<MeydanPostStateDto> & { event: 'meydan.post' }
  /** Meydan: my follows, follow requests or mutes changed (another device or someone else's answer). */
  | ServerEnvelope<Record<string, never>> & { event: 'meydan.updated' }
  /** "İstekler › Seni ekleyenler" changed: someone added me, or I added back, set aside or saw them. */
  | ServerEnvelope<Record<string, never>> & { event: 'contacts.requests' }
  /** Meydan: someone whose posts reach my following feed posted (a hint: the feeds named are read again). */
  | ServerEnvelope<MeydanNewPostEventDto> & { event: 'meydan.new' }
  /** Meydan: my activity page may have changed (a hint; `seenAt` when I marked it seen on another device). */
  | ServerEnvelope<{ seenAt?: string }> & { event: 'meydan.activity' }
  /** My scheduled messages in a chat changed (added, sent, failed, removed). */
  | ServerEnvelope<{ conversationId: string }> & { event: 'scheduled.updated' }
  /** My message reminders changed (set, moved, removed, fired); clients refetch them. */
  | ServerEnvelope<Record<string, never>> & { event: 'reminders.updated' }
  /** A reminder of mine is due now (shown in the app when it is open; the push covers the rest). */
  | ServerEnvelope<{ conversationId: string; messageId: string; title: string; body: string | null }> & { event: 'reminder.due' }
  /** My chat folders changed on another device; clients refetch them. */
  | ServerEnvelope<Record<string, never>> & { event: 'folders.updated' }
  | ServerEnvelope<{ reason: string }> & { event: 'session.revoked' }
  /** New to-device packets or device-list changes are waiting: the client calls /e2ee/sync. */
  | ServerEnvelope<Record<string, never>> & { event: 'e2ee.sync' }
  /** Someone I see changed their photo, name or about line: avatars and names refresh without a reload. */
  | ServerEnvelope<{ userId: string }> & { event: 'user.updated' }
  /** Someone I see got or lost a badge (or a badge type changed: userId null); badges are looked up again. */
  | ServerEnvelope<{ userId: string | null }> & { event: 'badges.updated' }
  /** My chat look changed on another device. */
  | ServerEnvelope<Record<string, never>> & { event: 'appearance.updated' }
  /** `ringUntil`: when the server gives up on it; a device that hears of it later (a frozen tab) does not ring. */
  | ServerEnvelope<{ callId: string; conversationId: string; caller: UserSummaryDto; media: CallMedia; sdp: string; ringUntil: string; device?: CallDeviceInfo }> & { event: 'call.incoming' }
  /** `device`: what the answering side is on (absent when its app did not say). */
  | ServerEnvelope<{ callId: string; sdp: string; device?: CallDeviceInfo }> & { event: 'call.answered' }
  /** The other side restarts the connection (network change, lost path): answer with call.renegotiate_answer. */
  | ServerEnvelope<{ callId: string; sdp: string }> & { event: 'call.renegotiate' }
  /** The answer to my call.renegotiate offer. */
  | ServerEnvelope<{ callId: string; sdp: string }> & { event: 'call.renegotiated' }
  /** One of the callee's devices is ringing now (the caller sees "Ringing" instead of "Calling"). */
  | ServerEnvelope<{ callId: string }> & { event: 'call.ringing' }
  /** People were added to my 1:1 call: it continues as a group call in this new group (join it right away). */
  | ServerEnvelope<{ callId: string; conversationId: string; title: string; media: CallMedia }> & { event: 'call.upgraded' }
  | ServerEnvelope<{ callId: string; candidate: { candidate: string; sdpMid?: string | null; sdpMLineIndex?: number | null } }> & { event: 'call.ice' }
  | ServerEnvelope<{ callId: string; reason: CallEndReason }> & { event: 'call.ended' }
  /** The other person took the call over on another of their devices: answer this new offer on a fresh connection. */
  | ServerEnvelope<{ callId: string; sdp: string; device?: CallDeviceInfo }> & { event: 'call.moved' }
  /** A group call started in one of my groups: it rings (unless I am already in a call). */
  | ServerEnvelope<{ call: GroupCallDto }> & { event: 'gcall.ringing' }
  /** Who is in a group's call changed, or it ended (`call` null): the chat's "join" bar follows. */
  | ServerEnvelope<{ conversationId: string; call: GroupCallDto | null }> & { event: 'gcall.updated' }
  /** Someone joined the call I am in: they will send me an offer. */
  | ServerEnvelope<{ callId: string; user: UserSummaryDto }> & { event: 'gcall.joined' }
  | ServerEnvelope<{ callId: string; userId: string }> & { event: 'gcall.left' }
  | ServerEnvelope<{ callId: string; from: string; data: GroupCallSignal }> & { event: 'gcall.signal' }
  /** A call of mine started or stopped on one of my devices; the others offer "move it here" while it runs. */
  | ServerEnvelope<{ callId: string; conversationId: string; peer: UserSummaryDto; media: CallMedia; active: boolean }> & { event: 'call.elsewhere' }
  | ServerEnvelope<{ reconnectAfterMs: number }> & { event: 'server.draining' }
  | ServerEnvelope<{ reqId: string; result: unknown }> & { event: 'ack' }
  | ServerEnvelope<{ reqId?: string; code: ErrorCode; message?: string }> & { event: 'error' }
  | ServerEnvelope<Record<string, never>> & { event: 'pong' };

// ---------- templates and AI writing assistant (ADR-016) ----------

export const templateInputSchema = z.object({
  title: z.string().trim().min(1).max(80),
  body: z.string().trim().min(1).max(2000),
  category: z.enum(TEMPLATE_CATEGORIES).default('general'),
});
export interface MessageTemplateDto {
  id: string;
  category: (typeof TEMPLATE_CATEGORIES)[number];
  title: string;
  body: string;
  /** The user's own template (editable); otherwise a built-in one. */
  own: boolean;
}

export type AiAction = (typeof AI_ACTIONS)[number];
/** Longest draft the assistant accepts. */
export const AI_MAX_INPUT_CHARS = 2000;
/** Bumped whenever the disclosure shown before consent changes: everyone is asked again. */
export const AI_CONSENT_VERSION = 'ai-2026-10-28';
export const aiRewriteSchema = z.object({ action: z.enum(AI_ACTIONS), text: z.string().trim().min(1).max(AI_MAX_INPUT_CHARS) });
export const aiConsentSchema = z.object({ consent: z.boolean() });
/** Languages a received message can be translated into ("Translate message"; BiP/Telegram style). */
export const TRANSLATION_LANGUAGES = ['tr', 'en', 'de', 'fr', 'es', 'ar', 'ru', 'it', 'pt', 'nl', 'az', 'ku'] as const;
export type TranslationLanguage = (typeof TRANSLATION_LANGUAGES)[number];
export const aiTranslateSchema = z.object({ text: z.string().trim().min(1).max(AI_MAX_INPUT_CHARS), target: z.enum(TRANSLATION_LANGUAGES) });
export interface AiTranslateResultDto {
  text: string;
  /** The language the model read the text as (BCP-47-ish, lower case), or null. */
  source: string | null;
  remainingToday: number;
}
export interface AiStatusDto {
  /** Turned on and a provider is configured on the server. */
  available: boolean;
  /** Why not, when unavailable: switched off in the panel, or no provider/key configured on the server. */
  unavailableReason: 'turned_off' | 'not_configured' | null;
  /** Adults with an active account only. */
  allowedForAccount: boolean;
  consented: boolean;
  consentVersion: string;
  provider: string | null;
  /** "Translate message" is on (its own flag) and the provider is available. */
  translation: boolean;
  dailyLimit: number;
  remainingToday: number;
}
export interface AiRewriteResultDto {
  text: string;
  /** Personal data items that were hidden from the provider. */
  redacted: number;
  /** Hidden items the model dropped from its answer (the user should check the text). */
  missingPlaceholders: number;
  remainingToday: number;
}

// ---------- calls (ADR-015) ----------

/** SDP offers/answers are a few KB; the cap keeps them well inside one WebSocket frame. */
export const MAX_SDP_LENGTH = 32 * 1024;
const sdpSchema = z.string().min(1).max(MAX_SDP_LENGTH);
const iceSchema = z.object({
  candidate: z.string().max(2048),
  sdpMid: z.string().max(64).nullable().optional(),
  sdpMLineIndex: z.number().int().min(0).max(64).nullable().optional(),
});
export type CallMedia = 'audio' | 'video';

/** What a call participant's device is, as its app tells it: only ever shown, never trusted for anything else. */
export const CALL_DEVICE_KINDS = ['phone', 'tablet', 'desktop'] as const;
export const CALL_DEVICE_APPS = ['pwa', 'browser'] as const;
export const CALL_DEVICE_OSES = ['ios', 'android', 'windows', 'macos', 'linux', 'chromeos'] as const;
export interface CallDeviceInfo {
  kind?: (typeof CALL_DEVICE_KINDS)[number];
  /** Installed app (home screen / desktop app) or a browser tab. */
  app?: (typeof CALL_DEVICE_APPS)[number];
  os?: (typeof CALL_DEVICE_OSES)[number];
}
/** Unknown values are dropped by the server rather than refused. */
const callDeviceSchema = z.object({ kind: z.string().max(16).optional(), app: z.string().max(16).optional(), os: z.string().max(16).optional() }).optional();

/** Group calls connect every device to every other one directly: beyond this many it gets too heavy. */
export const MAX_GROUP_CALL = 6;

export interface GroupCallDto {
  id: string;
  conversationId: string;
  media: CallMedia;
  startedBy: UserSummaryDto;
  startedAt: string;
  /** Who is in it now, in the order they joined. */
  participants: UserSummaryDto[];
  /** Each participant's device by user id, when their app said. */
  devices?: Record<string, CallDeviceInfo>;
}

/** Set-up messages between two people in a group call (relayed as they are). */
export const groupCallSignalSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('offer'), sdp: z.string().min(1).max(MAX_SDP_LENGTH) }),
  z.object({ type: z.literal('answer'), sdp: z.string().min(1).max(MAX_SDP_LENGTH) }),
  z.object({
    type: z.literal('ice'),
    candidate: z.object({
      candidate: z.string().max(2048),
      sdpMid: z.string().max(64).nullable().optional(),
      sdpMLineIndex: z.number().int().min(0).max(64).nullable().optional(),
      usernameFragment: z.string().max(256).nullable().optional(),
    }),
  }),
]);
export type GroupCallSignal = z.infer<typeof groupCallSignalSchema>;
export const groupCallStartSchema = z.object({ media: z.enum(['audio', 'video']), device: callDeviceSchema });
export type CallEndReason = 'hangup' | 'rejected' | 'missed' | 'busy' | 'cancelled' | 'failed' | 'disconnected' | 'answered_elsewhere' | 'moved';

export interface CallDto {
  id: string;
  conversationId: string;
  peer: UserSummaryDto;
  direction: 'outgoing' | 'incoming';
  media: CallMedia;
  state: 'ringing' | 'active' | 'ended';
  endReason: CallEndReason | null;
  createdAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  /** The other person's device in this call, when their app said. */
  peerDevice?: CallDeviceInfo;
}

/** What a device saw while connecting a call; sent once it connects or gives up (no addresses). */
export const CALL_CANDIDATE_TYPES = ['host', 'srflx', 'prflx', 'relay'] as const;
export const callReportSchema = z.object({
  outcome: z.enum(['connected', 'failed']),
  reason: z.enum(['ice_failed', 'timeout', 'no_relay']).optional(),
  localTypes: z.array(z.enum(CALL_CANDIDATE_TYPES)).max(4),
  remoteTypes: z.array(z.enum(CALL_CANDIDATE_TYPES)).max(4),
  /** How this device reached the TURN relay, when it got one. */
  relayProtocols: z.array(z.enum(['udp', 'tcp', 'tls'])).max(3),
  /** The pair that carried media (connected calls). */
  selected: z.object({ local: z.enum(CALL_CANDIDATE_TYPES), remote: z.enum(CALL_CANDIDATE_TYPES), protocol: z.enum(['udp', 'tcp']) }).nullable(),
  relayOnly: z.boolean(),
  ms: z.number().int().min(0).max(600_000),
});
export type CallReport = z.infer<typeof callReportSchema>;

/** What one device measured over a whole call, sent when it ends (POST /calls/{id}/quality; 1:1 or group). */
export const callQualitySchema = z.object({
  /** How media went: the selected pair's kind (relay when either end is) and transport (tls/tcp: to the relay). */
  candidateType: z.enum(CALL_CANDIDATE_TYPES).nullable().optional(),
  protocol: z.enum(['udp', 'tcp', 'tls']).nullable().optional(),
  rttMs: z.number().int().min(0).max(60_000).nullable().optional(),
  packetsLost: z.number().int().min(0).nullable().optional(),
  jitterMs: z.number().int().min(0).max(60_000).nullable().optional(),
  audioPacketsReceived: z.number().int().min(0).nullable().optional(),
  audioBytesSent: z.number().int().min(0).nullable().optional(),
  iceRestarts: z.number().int().min(0).max(1000).optional(),
  /** A stretch of connected call with no sound coming in. */
  noAudioDetected: z.boolean().optional(),
  device: callDeviceSchema,
  durationMs: z.number().int().min(0).nullable().optional(),
});
export type CallQualityReport = z.infer<typeof callQualitySchema>;

/** Admin view of call connectivity (counts only). */
export interface CallHealthDto {
  days: number;
  total: number;
  answered: number;
  connected: number;
  failed: number;
  missed: number;
  /** Failed connections by what the device reported. */
  failures: Record<string, number>;
  /** Devices that got a relay, by how they reached it. */
  relayVia: Record<string, number>;
  /** Devices that reported but got no relay at all (TURN unreachable or refused). */
  noRelay: number;
  turn: { urls: string[]; relayOnly: boolean; configured: boolean };
  /** Cloudflare's TURN relay: whether its key is on the server, and whether the switch is on. */
  cloudflare: { keyPresent: boolean; enabled: boolean; urls: string[]; error: string | null };
  /** The devices' own end-of-call summaries (absent when none came in). */
  quality?: {
    reports: number;
    noAudio: number;
    relay: number;
    restarted: number;
    medianRttMs: number | null;
    /** "relay/tls", "srflx/udp", … */
    paths: Record<string, number>;
    /** "phone/pwa/ios" ("?" for an unknown part). */
    devices: { device: string; reports: number; noAudio: number }[];
  };
}

export interface IceServersDto {
  iceServers: { urls: string[]; username?: string; credential?: string }[];
  /** Media must go through the relay (hides each side's IP address from the other). */
  relayOnly: boolean;
  ttlSeconds: number;
}

export const clientCommandSchema = z.discriminatedUnion('cmd', [
  z.object({ version: z.literal(PROTOCOL_VERSION), cmd: z.literal('ping'), req_id: z.string().max(64).optional() }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('message.send'),
    req_id: z.string().max(64),
    payload: sendMessageFields.extend({ conversationId: z.uuid() }).refine(notEmpty, { message: 'empty', path: ['body'] }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('receipt.read'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ conversationId: z.uuid(), seq: z.number().int().nonnegative() }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('typing'),
    payload: z.object({ conversationId: z.uuid(), typing: z.boolean() }),
  }),
  /** No activity for a while (or back): "idle" for the others. */
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('presence.idle'),
    payload: z.object({ idle: z.boolean() }),
  }),
  /** This window is in front and focused (or no longer): notifications are held back only while someone looks. */
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('presence.looking'),
    payload: z.object({ looking: z.boolean() }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('call.invite'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid(), conversationId: z.uuid(), media: z.enum(['audio', 'video']), sdp: sdpSchema, device: callDeviceSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('call.answer'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid(), sdp: sdpSchema, device: callDeviceSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    /** A fresh offer for my running call (ICE restart after a network change or a lost path). */
    cmd: z.literal('call.renegotiate'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid(), sdp: sdpSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    /** The answer to a call.renegotiate offer. */
    cmd: z.literal('call.renegotiate_answer'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid(), sdp: sdpSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    /** This device is ringing for an incoming call. */
    cmd: z.literal('call.ringing'),
    payload: z.object({ callId: z.uuid() }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    /** Take my running call over on this device (a fresh offer for the other person). */
    cmd: z.literal('call.move'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid(), sdp: sdpSchema, device: callDeviceSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    /** The answer to a call.moved offer. */
    cmd: z.literal('call.move_answer'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid(), sdp: sdpSchema, device: callDeviceSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    /** Connection set-up for one other person in my group call. */
    cmd: z.literal('gcall.signal'),
    payload: z.object({ callId: z.uuid(), to: z.uuid(), data: groupCallSignalSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('call.ice'),
    payload: z.object({ callId: z.uuid(), candidate: iceSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('call.reject'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid() }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('call.hangup'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid(), reason: z.enum(['hangup', 'failed']).default('hangup') }),
  }),
]);
export type ClientCommand = z.infer<typeof clientCommandSchema>;

export interface MessageChangesDto {
  /** Messages whose content changed after `since` (edited, deleted, reactions), oldest change first. */
  messages: MessageDto[];
  /** Messages this person removed for themselves (on another device) after `since`. */
  hiddenIds: string[];
  /** More changes than one page: ask again with since = nextSince. */
  hasMore: boolean;
  nextSince: string;
}

// ---------- broadcast lists ----------

/** Lists per person, and people per list (each send is one 1:1 message per person). */
export const MAX_BROADCAST_LISTS = 20;
export const MAX_BROADCAST_MEMBERS = 100;

export const broadcastListSchema = z.object({
  name: z.string().trim().min(1).max(60),
  memberIds: z.array(z.uuid()).max(MAX_BROADCAST_MEMBERS).default([]),
});
export const broadcastListUpdateSchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    memberIds: z.array(z.uuid()).max(MAX_BROADCAST_MEMBERS).optional(),
  })
  .refine((v) => v.name !== undefined || v.memberIds !== undefined, { message: 'nothing to change' });
export type BroadcastListInput = { name?: string | undefined; memberIds?: string[] | undefined };

export interface BroadcastListDto {
  id: string;
  name: string;
  createdAt: string;
  /** `reachable`: they have me in their contacts (and nobody blocked anyone), so a send reaches them. */
  members: { id: string; username: string; displayName: string; reachable: boolean }[];
}

/** A resumable upload (POST /conversations/:id/uploads, then PUT chunks, then POST …/complete). */
export const createUploadSchema = z.object({
  size: z.number().int().positive(),
  name: z.string().trim().min(1).max(255),
  /** Ciphertext of an end-to-end encrypted file (the key travels in the message). */
  encrypted: z.boolean().optional(),
  voice: z.boolean().optional(),
  durationMs: z.number().int().positive().optional(),
  round: z.boolean().optional(),
  waveform: z.array(z.number().int().min(0).max(100)).max(100).optional(),
  /** A photo sent as it is (no re-encoding, metadata kept), shown as a file. */
  asDocument: z.boolean().optional(),
  /** A picture sent as a sticker (one the person made). */
  sticker: z.boolean().optional(),
});
export type CreateUploadInput = z.infer<typeof createUploadSchema>;
export interface UploadSessionDto {
  id: string;
  size: number;
  /** Bytes the server has; the next chunk starts here. */
  received: number;
  chunkBytes: number;
}

/** How many stickers one person can keep in "Çıkartmalarım". */
export const MAX_MY_STICKERS = 60;
/** A sticker someone made; its picture is at /me/stickers/:id/image (theirs only). */
export interface StickerDto {
  id: string;
  createdAt: string;
}

// ---------- short links: dijitalapp.com/@name ----------

/** A group's or channel's short public name: 4–32 letters, digits, "_" and ".", like a username. */
export const HANDLE_PATTERN = /^[a-z0-9][a-z0-9_.]{2,30}[a-z0-9]$/;
export const setHandleSchema = z.object({
  handle: z
    .string()
    .trim()
    .toLowerCase()
    .transform((v) => v.replace(/^@/, ''))
    .pipe(z.string().regex(HANDLE_PATTERN))
    .nullable(),
});

/**
 * What dijitalapp.com/@name points at. Signed out, a person is only told as a kind; a group or channel shows what
 * its share card shows anyone (title, description, size, photo). `isPublic`: a public channel.
 */
export type LinkTargetDto =
  | { kind: 'user'; name: string; user: { id: string; username: string; displayName: string; isBot?: boolean } | null; isMe?: boolean }
  | {
      kind: 'group' | 'channel';
      name: string;
      /** slug: a channel's link (an @name of a channel answers it, and the app goes on to /kanal/<slug>); createdAt: channel links only. */
      conversation: { id: string; title: string; description: string | null; memberCount: number; member: boolean; hasPhoto: boolean; avatarUpdatedAt: string | null; verified?: boolean; isPublic: boolean; slug?: string; createdAt?: string } | null;
    };
